import { Component, inject, Input, OnChanges, OnInit, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { CommonUtils } from '../../../../common/common-utils';
import { LanguageTranslationState } from '../../../../../shared/models/dtc/language-translation-state';
import { NgClass } from '@angular/common';
import { AppInsightsService } from '../../../../services/app-insights.service';
import { DetailedTranslationLanguageStatistics } from './detailed-translation-language-statistics';
import { TranslationStateStatistics } from '../translation-state/translation-state-statistics';
import { ShareCalculator } from '../translation-state/share-calculator';

@Component({
  selector: 'app-detailed-translation-state',
  imports: [NgClass],
  templateUrl: './detailed-translation-state.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './detailed-translation-state.component.scss',
})
export class DetailedTranslationStateComponent implements OnChanges, OnInit {
  @Input() public translationStatesDocumentation?: LanguageTranslationState[];
  @Input() public translationStatesTerminal?: LanguageTranslationState[];

  public translationStatistics: DetailedTranslationLanguageStatistics[] = [];
  public commonUtils = CommonUtils;

  private appInsightsService = inject(AppInsightsService);

  public ngOnInit(): void {
    this.appInsightsService.logSidebarUsage('TranslationState');
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['translationStatesDocumentation'] && changes['translationStatesTerminal']) {
      const statesDocumentation = changes['translationStatesDocumentation'].currentValue as LanguageTranslationState[];
      if (!statesDocumentation) {
        this.translationStatistics = [];
        return;
      }

      const statesTerminal = changes['translationStatesTerminal'].currentValue as LanguageTranslationState[];
      if (!statesTerminal) {
        this.translationStatistics = [];
        return;
      }

      const shareCalculator = new ShareCalculator();

      const groupedData = new Map<string, DetailedTranslationLanguageStatistics>();
      statesDocumentation.forEach((i) => {
        const langCode = i.languageCode || 'unknown';
        const langStats =
          groupedData.get(langCode) ??
          ({
            languageCode: langCode,
            totalDocumentation: 0,
            statesDocumentation: [],
            totalTerminal: 0,
            statesTerminal: [],
          } as DetailedTranslationLanguageStatistics);
        langStats.totalDocumentation += i.count;
        langStats.statesDocumentation.push({
          translationState: i.translationState,
          count: i.count,
          percent: 0,
        } as TranslationStateStatistics);

        shareCalculator.RecalculateShares(langStats.totalDocumentation, langStats.statesDocumentation);
        shareCalculator.RecalculateSmallShares(langStats.statesDocumentation);

        langStats.statesDocumentation = langStats.statesDocumentation.sort((a, b) =>
          this.translationStateStatisticsSortOrder(a, b)
        );

        groupedData.set(langCode, langStats);
      });

      statesTerminal.forEach((i) => {
        const langCode = i.languageCode || 'unknown';
        const langStats = groupedData.get(langCode);
        if (langStats) {
          langStats.totalTerminal += i.count;
          langStats.statesTerminal.push({
            translationState: i.translationState,
            count: i.count,
            percent: 0,
          } as TranslationStateStatistics);

          shareCalculator.RecalculateShares(langStats.totalTerminal, langStats.statesTerminal);
          shareCalculator.RecalculateSmallShares(langStats.statesTerminal);

          langStats.statesTerminal = langStats.statesTerminal.sort((a, b) =>
            this.translationStateStatisticsSortOrder(a, b)
          );

          groupedData.set(langCode, langStats);
        }
      });

      this.translationStatistics = Array.from(groupedData.values()).sort((a, b) => {
        if (a.languageCode < b.languageCode) {
          return -1;
        }
        if (a.languageCode > b.languageCode) {
          return 1;
        }
        return 0;
      });
    }
  }

  private translationStateStatisticsSortOrder(a: TranslationStateStatistics, b: TranslationStateStatistics): number {
    if (
      (CommonUtils.translationStateDisplayConfig.get(a.translationState)?.displayOrder ?? 200) <
      (CommonUtils.translationStateDisplayConfig.get(b.translationState)?.displayOrder ?? 200)
    ) {
      return -1;
    }
    if (
      (CommonUtils.translationStateDisplayConfig.get(a.translationState)?.displayOrder ?? 200) >
      (CommonUtils.translationStateDisplayConfig.get(b.translationState)?.displayOrder ?? 200)
    ) {
      return 1;
    }
    return 0;
  }
}
